import { ErrorBoundary } from '@sentry/react'
import Styles from './DesignContentPreview.module.scss'
import { DesignContentPreviewProps, Ref } from './Types'
import { VisualEditor } from 'components/VisualStudioPlayground/VisualEditor/VisualEditor'
import { VisualEditorContext } from 'components/VisualStudioPlayground/VisualEditorContext'
import {
  applyPalette,
  getCurrentFrame,
  getLightestOrDarkestColor,
  setAutoplayVideo,
  setIsPreview,
} from 'components/VisualStudioPlayground/VisualEditor/helpers'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { toJS } from 'mobx'
import { DesignContentPreviewStore } from './DesignContentPreviewStore'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { insertImage } from 'components/Design/UploadsTab/insertImage'
import { ACCEPTED_VIDEO_MIME_TYPES } from 'screens/Finder/components/views/GalleryView/constants'
import { insertVideo } from 'components/Design/UploadsTab/insertVideo'
import { applyTextStyles } from 'components/VisualStudioPlayground/VisualEditor/utils/replaceFonts'
import { setCameraToCurrentFrame } from 'components/VisualStudioPlayground/Shapes/FrameShape/helpers'
import { IMediaFile } from 'components/Design/UploadsTab/Types'
import { StoreSnapshot, TLShapeId } from '@tldraw/tldraw'
import { ImageShape } from 'components/VisualStudioPlayground/Shapes/ImageShape/Types'
import { MaskShape } from 'components/VisualStudioPlayground/Shapes/MaskShape/Types'
import Color from 'colorjs.io'

const DesignContentPreview = observer(
  forwardRef<Ref, DesignContentPreviewProps>(
    ({ content, mediaFile, removeBackground, onLoadingStateChange, autoplayVideo = true }, fRef) => {
      const rootStore = useRootStore()
      const snapshot = toJS(content)
      const [store] = useState(() => new DesignContentPreviewStore(rootStore, snapshot))

      useImperativeHandle(
        fRef,
        () => {
          const update = async (callback: () => Promise<any>) => {
            if (store.store.status !== 'synced-local') {
              return
            }
            store.editor.updateInstanceState({ isReadonly: false, canMoveCamera: true })
            await callback()
            store.editor.selectNone()
            store.editor.updateInstanceState({ isReadonly: true, canMoveCamera: false })
            const snapshot = JSON.parse(JSON.stringify(store.editor.store.getSnapshot()))
            delete snapshot.store['page:page'].meta.isPreview
            delete snapshot.store['page:page'].meta.autoplayVideo
            return snapshot
          }
          return {
            getEditor: () => store.editor,
            applyPalette: (palette, startingColor?: Color) => {
              return update(async () => {
                const color = startingColor ?? getLightestOrDarkestColor(palette)
                applyPalette(store.editor, palette, color)
              })
            },
            zoomToFit: () =>
              update(async () => {
                const currentFrame = getCurrentFrame(store.editor)
                const bounds = store.editor.getShapePageBounds(currentFrame)
                if (bounds) {
                  const viewportScreenBounds = store.editor.getViewportScreenBounds()
                  store.editor.setCamera({
                    x: -bounds.x,
                    y: -bounds.y,
                    z: Math.min(viewportScreenBounds.width / bounds.width, viewportScreenBounds.height / bounds.height),
                  })
                } else {
                  store.editor.zoomToFit()
                }
              }),
            applyTextStyles: (styles, textBoxes) =>
              update(async () => applyTextStyles(store.editor, styles, textBoxes)),
            setCameraToCurrentFrame: (opts: {
              animate?: boolean
              isMobile: boolean
              padding?: { top: number; bottom: number }
            }) => update(async () => setCameraToCurrentFrame(store.editor, opts)),
            insertMediaFile: (mediaFile?: IMediaFile, removeBackground?: boolean) =>
              update(async () => {
                if (mediaFile) {
                  if (ACCEPTED_VIDEO_MIME_TYPES.includes(mediaFile.mime_type)) {
                    await insertVideo(store.editor, mediaFile, { replaceFit: 'cover' })
                  } else {
                    await insertImage(store.editor, mediaFile, {
                      removeBackground,
                      replaceReplaceable: true,
                      replaceReplaced: true,
                    })
                  }
                }
              }),
            loadSnapshot: (snapshot: StoreSnapshot<any>) => update(async () => await store.loadSnapshot(snapshot)),
            replaceMediaFile: (id: string, mediaFile: IMediaFile, removeBackground?: boolean) =>
              update(async () => {
                if (ACCEPTED_VIDEO_MIME_TYPES.includes(mediaFile.mime_type)) {
                  await insertVideo(store.editor, mediaFile, { replaceFit: 'cover' })
                } else {
                  const shape = store.editor.getShape(id as TLShapeId) as ImageShape | MaskShape | undefined
                  if (!shape) return
                  await insertImage(store.editor, mediaFile, {
                    removeBackground,
                    existingShape: shape,
                    fit: 'cover',
                  })
                }
              }),
          }
        },
        [store],
      )

      const loadingState = store.store.status
      useEffect(() => {
        onLoadingStateChange?.(loadingState)
      }, [loadingState, onLoadingStateChange])

      return (
        <ErrorBoundary>
          <div className={Styles.preview}>
            <div className={Styles.canvas}>
              <VisualEditorContext.Provider value={store.editor}>
                <VisualEditor
                  store={store.store}
                  onMount={(editor) => {
                    setIsPreview(editor, true)
                    setAutoplayVideo(editor, autoplayVideo)
                    store.setEditor(editor)
                    const currentFrame = getCurrentFrame(editor)
                    const bounds = editor.getShapePageBounds(currentFrame)
                    if (bounds) {
                      const viewportScreenBounds = editor.getViewportScreenBounds()
                      editor.setCamera({
                        x: -bounds.x,
                        y: -bounds.y,
                        z: Math.min(
                          viewportScreenBounds.width / bounds.width,
                          viewportScreenBounds.height / bounds.height,
                        ),
                      })
                    } else {
                      editor.zoomToFit()
                    }
                    editor.batch(async () => {
                      if (mediaFile) {
                        if (ACCEPTED_VIDEO_MIME_TYPES.includes(mediaFile.mime_type)) {
                          await insertVideo(editor, mediaFile, { replaceFit: 'cover' })
                        } else {
                          await insertImage(editor, mediaFile, { removeBackground, replaceReplaceable: true })
                        }
                      }
                      setTimeout(() => {
                        editor.selectNone()
                        editor.updateInstanceState({ isReadonly: true, canMoveCamera: false })
                      }, 0)
                    })
                  }}
                />
              </VisualEditorContext.Provider>
            </div>
            <div className={Styles.editorMask} />
          </div>
        </ErrorBoundary>
      )
    },
  ),
)

export { DesignContentPreview }
